import { lazy, Suspense, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Divider, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAppHomeData } from "../../utilities/service";
import { useAuth } from "../../hooks/AuthContext";
import { useLoading } from "../../hooks/LoadingContext";
import useApiLoader from "../../hooks/useApiLoader";
import { CommonSection } from "../../components/common/CommonSection";
import { UtsavHero } from "../../components/home/UtsavExperienceSections";

// ✅ Top section — load immediately (user sees these first)
import Banner from "../../components/home/Banner";
import CakeCategory from "../../components/home/Category";
import PopularCakes from "../../components/home/PopularCakes";

// ✅ Below-fold sections — lazy load (user hasn't scrolled there yet)
const OffersAndDeals = lazy(() => import("../../components/home/OffersAndDeals"));
const SubCategory = lazy(() => import("../../components/home/SubCategory"));
const BottomBanner = lazy(() => import("./banner/BottomBanner"));
const ExperienceFlavours = lazy(() => import("../../components/home/ExperienceFlavours"));
const TrendingCakes = lazy(() => import("./banner/TrendingCakes"));

// ✅ Simple fallback — invisible, no layout shift
const Empty = () => <Box sx={{ minHeight: "200px" }} />;

function Home() {
  const { setIsLoading, setProgress } = useLoading();
  const { user } = useAuth();
  const navigate = useNavigate();
  const regionReady = !!user?.region_id;

  // ✅ FIX 1: Track previous region to detect region switches
  const previousRegionId = useRef(user?.region_id);
  const regionChanged = previousRegionId.current !== user?.region_id;
  useEffect(() => {
    previousRegionId.current = user?.region_id;
  }, [user?.region_id]);

  // ✅ FIX 2: queryKey uses region_id (not region_name), staleTime:0, placeholderData:undefined
  const { isLoading: queryLoading, error, data } = useQuery({
    queryKey: ["getAppHomeData", user?.region_id, user?.customer_id],
    queryFn: () => getAppHomeData(user?.region_id, user?.customer_id ?? null),
    enabled: regionReady,
    staleTime: 0,
    placeholderData: undefined,
    onSettled: () => {
      setIsLoading(false);
      setProgress(100);
    },
  });

  // ✅ FIX 3: Safety net — clears loading even when React Query serves from cache
  useEffect(() => {
    if (!queryLoading && regionReady) {
      setIsLoading(false);
      setProgress(100);
    }
  }, [queryLoading, regionReady]);

  // ✅ FIX 4: isLoading includes regionChanged so stale content never renders
  const isLoading = !regionReady || queryLoading || regionChanged;
  useApiLoader(isLoading);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ Memoize all section data — not recalculated on every render
  const {
    banners,
    category,
    popularCakes,
    offers,
    flavours,
    specialFlavours,
    trendingCakes,
    generalBanner,
    bottomBanner,
  } = useMemo(() => ({
    banners: data?.data?.banners,
    category: data?.data?.sections?.[4],
    popularCakes: data?.data?.sections?.[0],
    offers: data?.data?.sections?.[1],
    flavours: data?.data?.sections?.[2],
    specialFlavours: data?.data?.sections?.[3],
    trendingCakes: data?.data?.sections?.[5],
    generalBanner: data?.data?.home_general_banner?.image_url,
    bottomBanner: data?.data?.home_bottom_banner,
  }), [data]);

  // ✅ Memoize handleBannerClick — not recreated on every render
  const handleBannerClick = useCallback((banner) => {
    if (!banner?.route_type || !banner?.route_item) return;

    const slugify = (str) =>
      str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");

    if (banner.route_type === "P") {
      const id = banner.route_item;
      const name = banner.route_data?.product_name || "product";
      navigate(`/product-description/${slugify(name)}/${id}`);
    } else if (banner.route_type === "SC") {
      const name = banner.route_data?.sub_category_name || "subcategory";
      navigate(`/listing/${slugify(name)}`);
    } else if (banner.route_type === "C") {
      const name = banner.route_data?.category_name || "category";
      navigate(`/listing/${slugify(name)}`);
    }
  }, [navigate]);

  // ✅ Memoize hasData — not recreated on every render
  const hasData = useCallback((section) => {
    return section?.products && section.products.length > 0;
  }, []);

  if (error) return <div>Error loading products</div>;

  // ✅ FIX 5: Block ALL rendering while switching region or loading — prevents blank/stale flash
  if (isLoading || !data) {
    return <Box sx={{ minHeight: "100vh" }} />;
  }

  return (
    <>
      <Box
        className="luxury-home-shell"
        sx={{
          position: "relative",
          marginTop: isMobile ? "24px" : "0px",
          pt: { xs: 2, md: 4 },
          pb: 6,
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "0 0 auto 0",
            height: "220px",
            background: "linear-gradient(180deg, rgba(242, 228, 194, 0.20) 0%, rgba(255,255,255,0) 100%)",
            pointerEvents: "none",
          },
        }}
      >

        {/* ✅ Banner — loads immediately, user sees this first */}
        {banners && banners.length > 0 && (
          <Banner
            items={banners}
            isLoading={false}
            onBannerClick={handleBannerClick}
          />
        )}

        <UtsavHero />

        {/* ✅ Category — loads immediately */}
        <CommonSection>
          <Box id="cake-category">
            <CakeCategory data={category} isLoading={false} />
          </Box>
        </CommonSection>

        {/* ✅ PopularCakes — loads immediately (visible on first scroll) */}
        {hasData(popularCakes) && (
          <CommonSection
            sx={{
              background:
                "linear-gradient(180deg, rgba(196, 154, 39, 0.08) 0%, rgba(247, 237, 208, 0.25) 100%)",
              [theme.breakpoints.down("sm")]: { marginTop: "16px" },
            }}
          >
            <Box id="popular-cakes">
              <PopularCakes data={popularCakes} isLoading={false} />
            </Box>
          </CommonSection>
        )}

        {/* ✅ Everything below is lazy — loads only when needed */}

        {hasData(offers) && (
          <CommonSection
            sx={{
              background:
                "linear-gradient(180deg, rgba(26, 26, 26, 0.04) 0%, rgba(247, 237, 208, 0.15) 100%)",
              [theme.breakpoints.down("sm")]: { marginTop: "16px" },
            }}
          >
            <Box id="offers-and-deals">
              <Suspense fallback={<Empty />}>
                <OffersAndDeals
                  data={offers}
                  isLoading={false}
                  generalBanner={generalBanner}
                />
              </Suspense>
            </Box>
          </CommonSection>
        )}

        {hasData(specialFlavours) && (
          <CommonSection>
            <Box id="special-flavours">
              <Suspense fallback={<Empty />}>
                <SubCategory data={specialFlavours} isLoading={false} />
              </Suspense>
            </Box>
          </CommonSection>
        )}

        {bottomBanner && (
          <CommonSection>
            <Box id="bottom-banner">
              <Suspense fallback={<Empty />}>
                <BottomBanner
                  items={bottomBanner}
                  isLoading={false}
                  onBannerClick={handleBannerClick}
                />
              </Suspense>
            </Box>
          </CommonSection>
        )}

        {hasData(flavours) && (
          <Box id="flavours">
            <Suspense fallback={<Empty />}>
              <ExperienceFlavours data={flavours} isLoading={false} />
            </Suspense>
          </Box>
        )}

        {hasData(trendingCakes) && (
          <Box id="trendingCakes">
            <Suspense fallback={<Empty />}>
              <TrendingCakes items={trendingCakes} isLoading={false} />
            </Suspense>
          </Box>
        )}

        <Divider variant="middle" sx={{ mx: 5 }} />



      </Box>
    </>
  );
}

export default Home;
